import React, { useState } from "react";
import { Sparkles, Download, Share2, X, Loader2, Eye } from "lucide-react";
import AIImageWithDisclaimer from "../AIImageWithDisclaimer";
import { bakeDisclaimerOnBase64 } from "../../utils/aiDisclaimer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MagicPreviewsPanel = ({ previews = [], phone, authAxios }) => {
  const [viewImage, setViewImage] = useState(null);
  const [loadingId, setLoadingId] = useState("");
  const [sharing, setSharing] = useState("");

  if (!previews || previews.length === 0) return null;

  const loadFullImage = async (imageId) => {
    setLoadingId(imageId);
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/magic-image/${imageId}`);
      setViewImage(res.data);
    } catch (e) {
      console.debug("[MagicPreviews] Image load failed:", e.message);
    }
    setLoadingId("");
  };

  const handleDownload = async (image) => {
    if (!image?.image_base64) return;
    const dataUrl = await bakeDisclaimerOnBase64(image.image_base64, "image/jpeg");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `doozygo-magic-${image.package}-${image.occasion}-${image.image_id}.jpg`;
    link.click();
  };

  const handleShareWhatsApp = async (imageId) => {
    setSharing(imageId);
    try {
      await authAxios.post(`${API_URL}/api/admin/magic-share-whatsapp`, {
        phone,
        image_id: imageId,
        message: `Hi! Here's your decoration preview from DoozyGo Magic. Love the look? Book now at www.doozygo.com`,
      });
      alert("Sent to customer's WhatsApp!");
    } catch (e) {
      alert("Failed to send: " + (e.response?.data?.detail || e.message));
    }
    setSharing("");
  };

  return (
    <div data-testid="magic-previews-panel">
      <h4 className="font-semibold text-[#1E1B4B] mb-2 flex items-center gap-2">
        <Sparkles size={14} className="text-[#E83E8C]" />
        Magic Previews ({previews.length})
      </h4>
      <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
        {previews.map((p) => (
          <div
            key={p.image_id}
            className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-[#E83E8C] transition-all cursor-pointer"
            data-testid={`magic-preview-${p.image_id}`}
          >
            {p.thumbnail_b64 ? (
              <img
                src={`data:image/jpeg;base64,${p.thumbnail_b64}`}
                alt={`${p.package} ${p.occasion}`}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-[#E83E8C]/10 to-purple-100 flex items-center justify-center">
                <Sparkles size={20} className="text-[#E83E8C]/40" />
              </div>
            )}
            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
              <button
                onClick={() => loadFullImage(p.image_id)}
                className="p-1.5 rounded-lg bg-white/90 text-[#1E1B4B] hover:bg-white transition-all"
                title="View full size"
                data-testid={`preview-view-${p.image_id}`}
              >
                {loadingId === p.image_id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              </button>
              <button
                onClick={() => handleShareWhatsApp(p.image_id)}
                className="p-1.5 rounded-lg bg-[#25D366]/90 text-white hover:bg-[#25D366] transition-all"
                title="Share via WhatsApp"
                data-testid={`preview-share-${p.image_id}`}
              >
                {sharing === p.image_id ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              </button>
            </div>
            {/* Label */}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1">
              <p className="text-white text-[9px] font-semibold truncate">{p.package}</p>
              <p className="text-white/70 text-[8px] truncate">{p.occasion}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Full Image Lightbox */}
      {viewImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setViewImage(null)}
          data-testid="magic-lightbox"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setViewImage(null)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100"
            >
              <X size={16} />
            </button>
            <AIImageWithDisclaimer
              src={`data:image/jpeg;base64,${viewImage.image_base64}`}
              alt={`${viewImage.package} ${viewImage.occasion}`}
              className="w-full rounded-xl shadow-2xl"
            />
            <div className="flex items-center justify-between mt-3 px-1">
              <div>
                <p className="text-white font-semibold text-sm">{viewImage.package} — {viewImage.occasion}</p>
                <p className="text-white/60 text-xs">{viewImage.created_at?.split("T")[0]} {viewImage.is_paid ? "(Paid)" : "(Free)"}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownload(viewImage)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[#1E1B4B] text-xs font-semibold hover:bg-gray-100"
                  data-testid="lightbox-download"
                >
                  <Download size={13} /> Download HD
                </button>
                <button
                  onClick={() => handleShareWhatsApp(viewImage.image_id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-xs font-semibold hover:bg-[#1da851]"
                  data-testid="lightbox-share"
                >
                  {sharing === viewImage.image_id ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
                  Send to Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
